import { Notification } from '@lib/components'
import {
  type NotificationOptions,
  notificationActionCreators,
  type ToastContainerId,
} from '@storeOld/reducers/notification'
import cx from 'classnames'
import type React from 'react'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { AppState } from '@/types'
import css from './NotificationContainer.module.scss'

const TOAST_CLEAR_TIME = 4000

interface Props {
  notification: NotificationOptions[]
  handleCloseCallback: (
    toastContainerId: ToastContainerId,
    index: number
  ) => void
}

const NotificationsElement: React.FC<Props> = ({
  notification,
  handleCloseCallback,
}) => (
  <>
    {notification.map((notify, index) => (
      <div className={css.notificationWrap} key={`${notify.text}/${index}`}>
        <Notification
          text={notify.text}
          type={notify.type}
          animated
          actionText={notify.actionText}
          {...(notify.callback && {
            onClick: () => {
              if (notify.callback) {
                notify.callback()
              }
              if (!notify.preventCloseAfterCallback) {
                handleCloseCallback(notify.toastContainerId, index)
              }
            },
          })}
          {...(notify.closeCallback && {
            onClose: () => {
              if (notify.closeCallback) {
                notify.closeCallback()
              }
              handleCloseCallback(notify.toastContainerId, index)
            },
          })}
        />
      </div>
    ))}
  </>
)

export const NotificationContainer: React.FC<{
  isPositionAbsolute?: boolean
}> = ({ isPositionAbsolute = false }) => {
  const dispatch = useDispatch()
  const notification = useSelector((state: AppState) => state.notification)

  useEffect(() => {
    const timer = setInterval(() => {
      dispatch(
        notificationActionCreators.setClearNotification({
          toastContainerId: 'secondary',
          index: 0,
        })
      )
    }, TOAST_CLEAR_TIME)

    if (notification.secondary.length === 0) {
      clearTimeout(timer)
    }

    return () => clearTimeout(timer)
  }, [notification])

  const handleCloseCallback = (
    toastContainerId: ToastContainerId,
    index: number
  ) => {
    dispatch(
      notificationActionCreators.setClearNotification({
        toastContainerId,
        index,
      })
    )
  }

  return (
    <div
      className={cx(css.container, 'notification', {
        [css.isPositionAbsolute]: isPositionAbsolute,
      })}
    >
      <div className={css.mainStack}>
        {notification.socket.length > 0 && (
          <NotificationsElement
            notification={notification.socket}
            handleCloseCallback={handleCloseCallback}
          />
        )}
        {notification.socket.length <= 0 && (
          <NotificationsElement
            notification={notification.main}
            handleCloseCallback={handleCloseCallback}
          />
        )}
      </div>
      <div className={css.secondaryStack}>
        <NotificationsElement
          notification={notification.secondary}
          handleCloseCallback={handleCloseCallback}
        />
      </div>
      <div className={css.secondaryStack}>
        <NotificationsElement
          notification={notification.ai}
          handleCloseCallback={handleCloseCallback}
        />
      </div>
    </div>
  )
}
